import React, { useEffect, useRef, useState } from 'react';
import { GraduationCap, HelpCircle, RefreshCw, Rocket, X } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { useTourStore } from '../stores/tourStore';
import { allowedWorkflowTours } from '../tours/access';
import {
  TOUR_DESCRIPTIONS,
  TOUR_TITLES,
  WORKFLOW_TOUR_DESCRIPTIONS,
  WORKFLOW_TOUR_TITLES,
  type WorkflowTourName,
} from '../tours/types';

interface TourHelpButtonProps { pathname: string }

export const TourHelpButton: React.FC<TourHelpButtonProps> = ({ pathname }) => {
  const [showPopover, setShowPopover] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const user = useAuthStore((state) => state.user);
  const {
    startTour, startWorkflowTour, hasTourForPage, getTourForPage, isTourCompleted,
    isWorkflowTourCompleted, isRunning, resetTour,
  } = useTourStore();
  const context = { role: user?.role, permissions: user?.permissions };
  const workflows = allowedWorkflowTours(context);
  const tourName = getTourForPage(pathname);
  const hasTour = hasTourForPage(pathname);
  const isCompleted = tourName ? isTourCompleted(tourName) : false;

  useEffect(() => {
    const closeOutside = (event: MouseEvent) => {
      if (!popoverRef.current?.contains(event.target as Node) && !buttonRef.current?.contains(event.target as Node)) setShowPopover(false);
    };
    document.addEventListener('mousedown', closeOutside);
    return () => document.removeEventListener('mousedown', closeOutside);
  }, []);

  if (isRunning) return null;

  const startPage = (reset: boolean) => {
    if (!tourName) return;
    if (reset) resetTour(tourName);
    setShowPopover(false);
    window.setTimeout(() => startTour(tourName), 100);
  };
  const startWorkflow = (name: WorkflowTourName, reset: boolean) => {
    if (reset) resetTour(name);
    setShowPopover(false);
    window.setTimeout(() => startWorkflowTour(name), 100);
  };
  const showIndicator = (hasTour && !isCompleted) || workflows.some((name) => !isWorkflowTourCompleted(name));
  const hasAnything = hasTour || workflows.length > 0;

  return <div className="relative">
    <button
      ref={buttonRef}
      onClick={() => setShowPopover((value) => !value)}
      className={`flex h-9 w-9 items-center justify-center rounded-lg transition ${showPopover ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300' : 'text-gray-400 hover:bg-indigo-50 hover:text-indigo-600 dark:text-zinc-500 dark:hover:bg-indigo-950/50 dark:hover:text-indigo-400'}`}
      title="Обучение"
    >
      <HelpCircle className="h-6 w-6" />
      {showIndicator && <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 animate-pulse rounded-full bg-indigo-500" />}
    </button>
    {showPopover && <div ref={popoverRef} className="absolute right-0 top-full z-50 mt-2 w-96 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl dark:border-zinc-800 dark:bg-zinc-900">
      <div className="flex items-center justify-between bg-gradient-to-r from-indigo-600 to-indigo-700 px-4 py-3">
        <div className="flex items-center gap-2 font-semibold text-white"><GraduationCap className="h-5 w-5" />Центр обучения</div>
        <button onClick={() => setShowPopover(false)} className="text-indigo-200 hover:text-white"><X className="h-5 w-5" /></button>
      </div>

      {workflows.length > 0 && <div className="border-b border-gray-100 p-4 dark:border-zinc-800">
        <div className="mb-3 flex items-center gap-2"><Rocket className="h-4 w-4 text-emerald-600" /><h4 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Сквозные туры</h4></div>
        <div className="space-y-2">{workflows.map((name) => {
          const completed = isWorkflowTourCompleted(name);
          return <div key={name} className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-900 dark:bg-emerald-950">
            <h5 className="text-sm font-medium text-gray-900 dark:text-zinc-100">{WORKFLOW_TOUR_TITLES[name]}</h5>
            <p className="mt-0.5 text-xs text-gray-600 dark:text-zinc-300">{WORKFLOW_TOUR_DESCRIPTIONS[name]}</p>
            <div className="mt-2 flex gap-2">
              <button onClick={() => startWorkflow(name, false)} className="flex-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700">{completed ? 'Пройти снова' : 'Начать тур'}</button>
              {completed && <button onClick={() => startWorkflow(name, true)} title="Сбросить прогресс" className="rounded-lg p-2 text-gray-500 hover:bg-emerald-100"><RefreshCw className="h-4 w-4" /></button>}
            </div>
          </div>;
        })}</div>
      </div>}

      {hasTour && tourName && <div className="p-4">
        <div className="mb-3 flex items-center gap-2"><GraduationCap className="h-4 w-4 text-indigo-600" /><h4 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Обучение по разделу</h4></div>
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-zinc-800 dark:bg-zinc-950">
          <h5 className="text-sm font-medium text-gray-900 dark:text-zinc-100">{TOUR_TITLES[tourName]}</h5>
          <p className="mt-0.5 text-xs text-gray-600 dark:text-zinc-300">{TOUR_DESCRIPTIONS[tourName]}</p>
          <div className="mt-2 flex gap-2">
            <button onClick={() => startPage(false)} className="flex-1 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700">{isCompleted ? 'Пройти снова' : 'Начать обучение'}</button>
            {isCompleted && <button onClick={() => startPage(true)} title="Сбросить прогресс" className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-zinc-800"><RefreshCw className="h-4 w-4" /></button>}
          </div>
        </div>
      </div>}

      {!hasAnything && <div className="p-4 text-xs text-gray-500 dark:text-zinc-400">Для этого раздела обучения пока нет.</div>}
      {workflows.length > 0 && <div className="border-t border-gray-100 bg-gray-50 px-4 py-3 text-xs text-gray-500 dark:border-zinc-800 dark:bg-zinc-950 dark:text-zinc-400">Сквозной тур проведёт вас через связанный процесс работы.</div>}
    </div>}
  </div>;
};

export default TourHelpButton;
